/* eslint-disable react/no-unused-prop-types */
import {
    ReactNode, useCallback, useMemo,
} from 'react';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import { useReduxForm } from '@rainbow-modules/hooks';
import { Option } from 'react-rainbow-components';
import Picklist from 'components/Picklist';
import useHttpQuery from 'data/firestore/useHttpQuery';
import { ACTIONS_BASE_URL } from '../../constants';

interface Workflow {
    id: string;
    name: string;
    enabled: boolean;
    triggerType: string | null;
}

interface WorkflowPickerProps {
    id?: string;
    className?: string;
    name?: string;
    label?: string;
    tabIndex?: number | string;
    placeholder?: string;
    error?: ReactNode;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    value?: string;
    agentId?: string;
    onChange?: (value: string) => void;
}

const WorkflowPicker = (props: WorkflowPickerProps) => {
    const {
        value: valueInProps,
        onChange = () => {},
        id,
        className,
        name,
        label,
        tabIndex,
        placeholder,
        error,
        required,
        disabled,
        readOnly,
        agentId,
    } = useReduxForm(props);

    const { data, isLoading } = useHttpQuery<{ data: Workflow[] }>({
        pathname: `/workflows?agentId=${agentId}`,
        server: ACTIONS_BASE_URL,
        queryOptions: {
            enabled: Boolean(agentId),
        },
    });

    const workflows = (data?.data ?? []).filter(
        (w) => w.triggerType === 'twilio-voice-trigger',
    );

    const pickListValue = useMemo(() => {
        if (valueInProps) {
            const found = workflows.find((w) => w.id === valueInProps);
            if (found) return { name: found.id, label: found.name, value: found.id };
        }
        return undefined;
    }, [valueInProps, workflows]);

    const handleOnChange = useCallback(
        (value: PicklistValue) => onChange(value.value as string),
        [onChange],
    );

    return (
        <Picklist
            id={id}
            name={name}
            className={className}
            label={label}
            labelAlignment="left"
            tabIndex={tabIndex}
            required={required}
            placeholder={placeholder}
            value={pickListValue}
            disabled={disabled || isLoading}
            readOnly={readOnly}
            onChange={handleOnChange}
            error={error}
            borderRadius="semi-rounded"
        >
            <Option label="Select Workflow" variant="header" />
            {workflows.map((workflow) => (
                <Option
                    key={workflow.id}
                    name={workflow.id}
                    label={workflow.name}
                    value={workflow.id}
                />
            ))}
        </Picklist>
    );
};

export default WorkflowPicker;
